import { Form, Menu } from '@mairistem/composants';
import { FicheBar, FicheContainerContext } from '@mairistem/fiche';
import { isNewIdentifiant, request } from '@mairistem/tools';
import _ from 'lodash';
import React from 'react';
import { useHistory } from 'react-router';
import { toast } from 'react-toastify';
import { MODES_REGLEMENTS, REGIE } from '@/constants';
import { ModeReglementContext, RegieContext } from '@/contexts';
import { baseUrlFront, getTresoreries, uploadDocument } from '@/routes';
import type {
  ApiResponseType,
  NewlyAddedFileType,
  RegieLimitCheckType,
  RegieType,
  TresorerieType,
} from '@/types';
import { checkRegieLimit } from '@/utils';
import { useRegieStore } from '@/zustand';
import ModalChoixTypeRegie from '../ModalChoixTypeRegie';
import ModalRegieEcoleVersProduction from '../ModalRegieEcoleVersProduction';

interface ConstitutionRegieFicheBarProps {
  identifiant?: number | string;
  regie?: RegieType;
  loading?: boolean;
  onChange?: (data: any, shouldChangeStatus?: boolean) => void;
  onSaved?: (...args: any) => void;
}

const ConstitutionRegieFicheBar = (props: ConstitutionRegieFicheBarProps) => {
  const { identifiant, regie, onChange, onSaved, loading, ...rest } = props;

  const [loadingTresorerieExistante, setLoadingTresorerieExistante] = React.useState(false);
  const [tresoExistante, setTresoExistante] = React.useState<TresorerieType>();
  const [autonomeCreation, setAutonomeCreation] = React.useState<boolean | null>(null);
  const [checkLimitResult, setCheckLimitResult] = React.useState<RegieLimitCheckType | null>(null);

  const history = useHistory();

  const { status, onChangeStatus } = React.useContext(FicheContainerContext);
  const { created, updated } = status || {};

  const { selectRegie } = React.useContext(RegieContext);
  const { modesReglementsActifs, getRegieModesReglement } = React.useContext(ModeReglementContext);

  const updateDisabledMenus = useRegieStore((regieStore) => regieStore.updateDisabledMenus);
  const setHasRegies = useRegieStore((regieStore) => regieStore.setHasRegies);
  const hasRegies = useRegieStore((regieStore) => regieStore.hasRegies);
  const selectedRegieId = useRegieStore((regieStore) => regieStore.regie?.identifiant);

  // Ref pour ne forcer created:true qu'une seule fois par identifiant
  const hasAutoCreatedRef = React.useRef(false);

  // Force le mode création (created:true) pour le flux d'auto-création
  // (arrivée sur un nouvel identifiant via redirect, sans clic sur "+")
  // useLayoutEffect s'exécute AVANT les useEffect de FicheProvider,
  // empêchant handleCreateNew d'être appelé et de corrompre les données
  React.useLayoutEffect(() => {
    if (isNewIdentifiant(identifiant) && !created && !hasAutoCreatedRef.current) {
      onChangeStatus?.((old) => ({ ...old, created: true }));
      hasAutoCreatedRef.current = true;
    }
    if (!isNewIdentifiant(identifiant)) {
      hasAutoCreatedRef.current = false;
    }
  }, [identifiant, created, onChangeStatus]);

  const getTresorerieExistante = React.useCallback(() => {
    setLoadingTresorerieExistante(true);

    request
      .get(getTresoreries())
      .then(({ data }: ApiResponseType<TresorerieType>) => {
        const treso = _.head(data);
        setTresoExistante(treso);
      })
      .finally(() => {
        setLoadingTresorerieExistante(false);
      });
  }, []);

  const handleCreate = React.useCallback(async () => {
    try {
      const response: RegieLimitCheckType = await checkRegieLimit();
      const { facturier, autonome } = response;

      if (!facturier.canCreate && !autonome.canCreate) {
        toast.error(
          'Veuillez-vous rapprocher de votre interlocuteur commercial habituel afin de pouvoir créer une nouvelle régie.',
        );
        return false;
      }

      setCheckLimitResult(response);
      return undefined;
    } catch {
      toast.error('Impossible de vérifier la limite de régies. Création bloquée par sécurité.');
      return false;
    }
  }, []);

  const handleTypeRegieSelect = React.useCallback(
    (autonome: boolean) => {
      setAutonomeCreation(autonome);
      onChange?.({ identifiant, autonome });
    },
    [identifiant, onChange],
  );

  const handleCancelCreation = React.useCallback(() => {
    if (selectedRegieId) {
      history.push(`${baseUrlFront}/constitution/${selectedRegieId}`);
    } else {
      // Retour à l'accueil 360
      history.push('/');
    }
  }, [history, selectedRegieId]);

  const handleCheckboxChange = React.useCallback(
    (e, { checked }) => {
      if (onChange) {
        onChange({
          identifiant,
          ecole: checked,
        });
      }
    },
    [identifiant, onChange],
  );

  /**
   * On reformate certaines données avant de les envoyer à l'API
   */
  const handleSaving = React.useCallback(
    (data) => ({
      ...data,
      seuilRelance: _.toNumber(_.replace(data.seuilRelance, ',', '.')),
      seuilRecouvrement: _.toNumber(_.replace(data.seuilRecouvrement, ',', '.')),
      ...(data.modesReglements && {
        modesReglements: _.map(data.modesReglements, (mode) => ({
          identifiantModeReglement: mode.identifiant,
          ordre: mode.ordre,
        })),
      }),
    }),
    [],
  );

  const handleSaved = React.useCallback(
    (...args) => {
      const [savedData, statusSaved] = args;

      // On met à jour le contexte global pour se placer sur la régie qu'on vient de créer
      if (statusSaved?.created === true) {
        setHasRegies(true);
      }
      selectRegie(savedData);

      const promises = [];

      // On réactualise les modes de règlements
      promises.push(getRegieModesReglement());

      // On upload chaque document se trouvant dans addedDocuments
      _.forEach(regie.addedDocuments as NewlyAddedFileType[], (document) => {
        const formData = new FormData();
        formData.append('file', document.fichier, document.fichier.name);

        promises.push(
          request.upload(
            uploadDocument({
              titre: document.titre,
              nomTableLiee: 'regie',
              identifiantTableLiee: savedData.identifiant,
              categorie: 'regie',
            }),
            formData,
          ),
        );
      });

      // On upload le logo s'il y en a un
      if (!_.isNil(regie.logo)) {
        // Convertir la chaîne base64 en Blob
        const byteString = atob(regie.logo.split(',')[1]);
        const mimeString = regie.logo.split(',')[0].split(':')[1].split(';')[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i += 1) {
          ia[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([ab], { type: mimeString });

        const formData = new FormData();
        const extension = mimeString.split('/')[1];
        formData.append('file', blob, `logo.${extension}`);

        request.upload(
          uploadDocument({
            titre: `Logo de ${savedData.titre}`,
            nomTableLiee: 'regie',
            identifiantTableLiee: savedData.identifiant,
            categorie: 'logo',
            replaceExisting: true,
          }),
          formData,
        );
      }

      Promise.all(promises).finally(() => {
        updateDisabledMenus(savedData);

        onSaved?.(...args);
      });
    },
    [regie, getRegieModesReglement, onSaved, selectRegie, setHasRegies, updateDisabledMenus],
  );

  React.useEffect(() => {
    getTresorerieExistante();
  }, [getTresorerieExistante]);

  // Reset du choix autonome quand on arrive sur une nouvelle création
  React.useEffect(() => {
    if (isNewIdentifiant(identifiant)) {
      setAutonomeCreation(null);
      setCheckLimitResult(null);
    }
  }, [identifiant]);

  // Vérification des limites pour la création (redirect sans clic "+" ou après clic "+")
  React.useEffect(() => {
    if (!isNewIdentifiant(identifiant) || checkLimitResult !== null) return;

    checkRegieLimit()
      .then((response: RegieLimitCheckType) => {
        setCheckLimitResult(response);
      })
      .catch(() => {
        toast.error('Impossible de vérifier la limite de régies. Création bloquée par sécurité.');
      });
  }, [identifiant, checkLimitResult]);

  // Auto-sélection du type si un seul est disponible
  React.useEffect(() => {
    if (!isNewIdentifiant(identifiant) || autonomeCreation !== null || !checkLimitResult) return;

    const { facturier, autonome } = checkLimitResult;

    if (facturier.canCreate && !autonome.canCreate) {
      handleTypeRegieSelect(false);
    } else if (!facturier.canCreate && autonome.canCreate) {
      handleTypeRegieSelect(true);
    }
  }, [identifiant, autonomeCreation, checkLimitResult, handleTypeRegieSelect]);

  /** Met des valeurs par défaut dans la fiche si on est en création
   * On n'utilise pas le onCreating de FicheBar car on peut arriver en création autrement
   * que par le + de FicheBar (première création de régie)
   * On attend que le choix du type de régie (autonome) soit fait avant d'appliquer les defaults
   */
  React.useEffect(() => {
    if (
      !loading &&
      !loadingTresorerieExistante &&
      isNewIdentifiant(identifiant) &&
      autonomeCreation !== null
    ) {
      onChange({
        identifiant,
        ecole: true,
        autonome: autonomeCreation,
        protocoleImpaye: REGIE.PROTOCOLE_IMPAYE_PES_ASAP,
        comptabilite: REGIE.COMPTABILITE_JVS,
        seuilRecouvrement: 15,
        regisseurs: [],
        modesReglements: _.map(
          _.filter(
            modesReglementsActifs,
            (mode) =>
              mode.code === MODES_REGLEMENTS.CHEQUE || mode.code === MODES_REGLEMENTS.NUMERAIRE,
          ),
          (mode, i) => ({ ..._.omit(mode, 'actif'), ordre: i }),
        ),
        tresorerie: tresoExistante,
      });
    }
  }, [
    identifiant,
    modesReglementsActifs,
    onChange,
    loading,
    loadingTresorerieExistante,
    tresoExistante,
    autonomeCreation,
  ]);

  return (
    <>
      <FicheBar
        key="ConstitutionRegieFicheBar"
        type="regie"
        identifiant={identifiant}
        canDraft={false}
        canDelete={false}
        {...rest}
        onCreating={handleCreate}
        onSaving={handleSaving}
        onSaved={handleSaved}
      >
        {created ? (
          <Menu.Item>
            <Form size="big">
              <Form.Checkbox
                toggle
                checked={regie?.ecole}
                label="Régie test"
                style={{ fontSize: 16 }}
                onChange={handleCheckboxChange}
              />
            </Form>
          </Menu.Item>
        ) : (
          regie?.ecole && (
            <ModalRegieEcoleVersProduction
              disabled={updated}
              regie={regie}
              onConfirm={(d) => onChange(d, false)}
            />
          )
        )}
      </FicheBar>
      {isNewIdentifiant(identifiant) &&
        autonomeCreation === null &&
        checkLimitResult?.facturier?.canCreate &&
        checkLimitResult?.autonome?.canCreate && (
          <ModalChoixTypeRegie
            onSelect={handleTypeRegieSelect}
            onCancel={handleCancelCreation}
            canCancel={hasRegies}
          />
        )}
    </>
  );
};

export default ConstitutionRegieFicheBar;
